import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import React, { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { DiscountedItem, DiscountedItemCategory } from 'modules/campaigns/hooks/useDiscountedItems';

import { DiscountedItemsGrid } from './DiscountedItemsGrid';
import { DiscountedItemsModal } from './DiscountedItemsModal';
import CampaignFormItem from '../CampaignFormItem';
import { DiscountedItemCountSelector } from '../discounted-item-section/DiscountedItemCountSelector/DiscountedItemCountSelector';
import EndOfDayItemsCountField from '../discounted-item-section/DiscountedItemFormItem/EndOfDayItemsCountField';
import UnsavedChangesModal from '../modals/UnsavedChangesModal/UnsavedChangesModal';

type FormValues = { itemIds: string[]; categoryIds: string[] };

type DiscountedItemsFormItemProps = {
  selectedItemIds: string[];
  selectedCategoryIds: string[];
  parentFormFieldNames: string[];
  onChange: (values: FormValues) => void;
  discountedItems: DiscountedItem[];
  discountedItemCategories: DiscountedItemCategory[];
  isLoadingMenuData: boolean;
  hasErrorMenuData: boolean;
  disabled: boolean;
  hasSubmitError?: boolean;
  isEndOfDayCampaign?: boolean;
  'data-test-id'?: string;
};

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
  margin-block-start: 16px;
`;

const SecondaryItemContainer = styled.div`
  width: 100%;
`;

const Divider = styled.div`
  margin-inline: 16px;
  border-block-start: 1px solid ${color.border};
`;

const SecondaryItemContent = styled.div`
  padding: 16px;
`;

const SelectItemsButton = styled(Button)`
  align-self: flex-start;
`;

export const DiscountedItemsFormItem = ({
  selectedItemIds,
  selectedCategoryIds,
  parentFormFieldNames,
  onChange,
  discountedItems,
  discountedItemCategories,
  isLoadingMenuData,
  hasErrorMenuData,
  disabled,
  hasSubmitError,
  isEndOfDayCampaign,
  ...props
}: DiscountedItemsFormItemProps) => {
  const [isDiscountedItemSelectorModalOpen, setIsDiscountedItemSelectorModalOpen] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);
  const handleCloseModals = (isFormDirty?: boolean) => {
    if (isFormDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      setIsUnsavedChangesModalOpen(false);
      setIsDiscountedItemSelectorModalOpen(false);
    }
  };

  const handleDiscountedItemUpdate = (values: FormValues) => {
    onChange(values);
    handleCloseModals();
  };

  const hasSelection = !!selectedItemIds.length || !!selectedCategoryIds.length;

  const initialValues = useMemo(
    () => ({ itemIds: selectedItemIds, categoryIds: selectedCategoryIds }),
    [selectedItemIds, selectedCategoryIds],
  );

  return (
    <>
      {/* Register the fields to make final-form consider parent the form dirty when changed */}
      {parentFormFieldNames.map(fieldName => (
        <Field key={fieldName} name={fieldName}>
          {() => null}
        </Field>
      ))}
      <Form<FormValues> onSubmit={handleDiscountedItemUpdate} initialValues={initialValues}>
        {({ handleSubmit, dirty, form, values }) => (
          <>
            {/* Register the fields to make final-form consider the modal form dirty when changed */}
            {Object.keys(initialValues).map(fieldName => (
              <Field key={fieldName} name={fieldName}>
                {() => null}
              </Field>
            ))}
            <DiscountedItemsModal
              discountedItems={discountedItems}
              discountedItemCategories={discountedItemCategories}
              isLoadingMenuItems={isLoadingMenuData}
              isOpen={isDiscountedItemSelectorModalOpen}
              selectedItemIds={values.itemIds}
              selectedCategoryIds={values.categoryIds}
              onSelectionChange={({ itemIds, categoryIds }) => {
                form.batch(() => {
                  form.change('itemIds', itemIds);
                  form.change('categoryIds', categoryIds);
                });
              }}
              onSubmit={handleSubmit}
              onDismiss={() => handleCloseModals(dirty)}
              onCancel={() => {
                handleCloseModals();
                form.reset();
              }}
            />
            <UnsavedChangesModal
              isOpen={dirty && isUnsavedChangesModalOpen}
              onDropChanges={() => {
                handleCloseModals();
                form.reset();
              }}
              onClose={() => setIsUnsavedChangesModalOpen(false)}
            />
          </>
        )}
      </Form>
      <CampaignFormItem
        {...props}
        title={<FormattedMessage id="campaigns.form.item.which-items-have-discount" />}
        subtitle={<FormattedMessage id="campaigns.form.item.select-items-description" />}
        action={
          hasSelection && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => setIsDiscountedItemSelectorModalOpen(true)}
              disabled={disabled}
            >
              <FormattedMessage id="campaigns.form.item.change-items" />
            </Button>
          )
        }
        value={
          <ItemContainer>
            {hasErrorMenuData ? (
              <Alert variant="error">
                <FormattedMessage id="campaigns.menu-unavailable" />
              </Alert>
            ) : hasSelection ? (
              <DiscountedItemsGrid
                selectedItemIds={selectedItemIds}
                selectedCategoryIds={selectedCategoryIds}
                discountedItems={discountedItems}
                discountedItemCategories={discountedItemCategories}
                isLoadingMenuData={isLoadingMenuData}
                onEditClick={() => setIsDiscountedItemSelectorModalOpen(true)}
              />
            ) : (
              <SelectItemsButton
                variant="primary"
                size="large"
                onClick={() => setIsDiscountedItemSelectorModalOpen(true)}
              >
                <FormattedMessage id="campaigns.form.item.select-items" />
              </SelectItemsButton>
            )}
            {hasSubmitError && (
              <Alert variant="error">
                <FormattedMessage id="validation.required" />
              </Alert>
            )}
          </ItemContainer>
        }
      >
        {hasSelection && (
          <SecondaryItemContainer>
            <Divider />
            <SecondaryItemContent>
              {isEndOfDayCampaign ? <EndOfDayItemsCountField /> : <DiscountedItemCountSelector disabled={disabled} />}
            </SecondaryItemContent>
          </SecondaryItemContainer>
        )}
      </CampaignFormItem>
    </>
  );
};
